import React, { forwardRef, useContext, useState } from 'react';
import { GlobalContext } from '../../contexts/GlobalContext';

const CoffeeChatQuestions = forwardRef((props, ref) => {
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [showGenerator, setShowGenerator] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastFading, setToastFading] = useState(false);
  const { userProfile, userSchool } = useContext(GlobalContext);

  const questions = [
    'What does your typical day-to-day look like in your role?',
    'How did you get started in this industry/company?',
    'What skills or experiences have been most valuable in your career?',
    'What challenges are you currently facing in your role or industry?',
    'What advice would you give to someone looking to enter this field?',
    'How do you see the industry evolving in the next few years?',
    'What resources or communities would you recommend for someone in my position?',
    "Can you tell me about a standout project you've worked on in your career?",
    'What surprised you most about working in this industry?',
    "Is there anyone else you'd recommend I connect with to learn more?",
  ];

  const generateLinkedInMessage = () => {
    if (!userProfile) return;

    const firstName = userProfile.first_name;
    const school = userSchool || 'my university';
    const originalQuestion = questions[selectedQuestion];

    // Create natural lead-ins and rephrase questions for better flow
    const leadIns = [
      "I'm curious about",
      "I'd love to learn about",
      "I'm particularly interested in",
      "I'd appreciate your insights on",
      "I'm wondering about",
    ];

    // Rephrase questions to work better with lead-ins
    const rephraseQuestion = (question) => {
      const rephrases = {
        'What does your typical day-to-day look like in your role?':
          'what your typical day-to-day looks like',
        'How did you get started in this industry/company?': 'how you got started in your industry',
        'What skills or experiences have been most valuable in your career?':
          'what skills have been most valuable in your career',
        'What challenges are you currently facing in your role or industry?':
          'the current challenges in your industry',
        'What advice would you give to someone looking to enter this field?':
          'any advice for someone entering this field',
        'How do you see the industry evolving in the next few years?':
          'how you see the industry evolving',
        'What resources or communities would you recommend for someone in my position?':
          "what resources you'd recommend for students",
        "Can you tell me about a standout project you've worked on in your career?":
          "standout projects you've worked on",
        'What surprised you most about working in this industry?':
          'what surprised you most about your industry',
        "Is there anyone else you'd recommend I connect with to learn more?":
          'your career journey and any networking advice',
      };
      return rephrases[question] || question.toLowerCase().replace(/\?$/, '');
    };

    const rephrasedQuestion = rephraseQuestion(originalQuestion);
    const randomLeadIn = leadIns[Math.floor(Math.random() * leadIns.length)];

    // Create more natural message templates
    const templates = [
      `Hi! I'm ${firstName}, a student at ${school}. ${randomLeadIn} ${rephrasedQuestion}. I'd love to connect and chat some more!`,
      `Hello! I'm ${firstName} from ${school}. Would love to chat about your career. ${randomLeadIn} ${rephrasedQuestion}!`,
      `Hi! ${firstName} here, studying at ${school}. ${randomLeadIn} ${rephrasedQuestion}. Would you be open to connecting?`,
      `Hi! ${firstName} from ${school}. I'd appreciate connecting to learn more about ${rephrasedQuestion}.`,
    ];

    // Filter templates that fit under 200 characters, then randomly choose one
    const validTemplates = templates.filter((template) => template.length <= 200);
    let bestMessage;

    if (validTemplates.length > 0) {
      // Randomly select from valid templates
      bestMessage = validTemplates[Math.floor(Math.random() * validTemplates.length)];
    } else {
      // If no templates fit, we'll handle this in the next block
      bestMessage = templates[0];
    }

    // If still too long, use a very concise version
    if (bestMessage.length > 200) {
      const baseMessage = `Hi! I'm ${firstName} from ${school}. ${randomLeadIn} ${rephrasedQuestion}.`;
      if (baseMessage.length <= 200) {
        bestMessage = baseMessage;
      } else {
        // Last resort - truncate the rephrased question
        const maxQuestionLength =
          200 - `Hi! I'm ${firstName} from ${school}. ${randomLeadIn} `.length - 1;
        const truncatedQuestion =
          rephrasedQuestion.length > maxQuestionLength
            ? rephrasedQuestion.substring(0, maxQuestionLength - 3) + '...'
            : rephrasedQuestion;
        bestMessage = `Hi! I'm ${firstName} from ${school}. ${randomLeadIn} ${truncatedQuestion}.`;
      }
    }

    setGeneratedMessage(bestMessage);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedMessage);
      setShowToast(true);
      setToastFading(false);

      // Start fade out after 2.5 seconds
      setTimeout(() => setToastFading(true), 2500);

      // Hide completely after fade animation
      setTimeout(() => {
        setShowToast(false);
        setToastFading(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="w-full max-w-6xl mt-8" ref={ref}>
      {/* Toast notification */}
      {showToast && (
        <div
          className={`fixed top-4 right-4 z-50 bg-white border border-gray-200 text-gray-800 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 transition-opacity duration-500 ${toastFading ? 'opacity-0' : 'opacity-100'}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-green-500"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Message copied to clipboard!
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-xs">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800 flex items-center">
              <span className="text-xl me-2.5">☕</span>
              Common Coffee Chat Questions
            </h3>
            <button
              onClick={() => setShowGenerator(!showGenerator)}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              📨 Generate LinkedIn Message
            </button>
          </div>

          {showGenerator && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-md font-medium text-gray-800 mb-3">LinkedIn Message Generator</h4>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select a question to include:
                </label>
                <select
                  value={selectedQuestion}
                  onChange={(e) => setSelectedQuestion(parseInt(e.target.value))}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {questions.map((question, index) => (
                    <option key={index} value={index}>
                      {index + 1}.{' '}
                      {question.length > 50 ? question.substring(0, 50) + '...' : question}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={generateLinkedInMessage}
                disabled={!userProfile}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium mb-3"
              >
                Generate Message
              </button>

              {generatedMessage && (
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Generated Message:</span>
                    <span className="text-xs text-gray-500">
                      {generatedMessage.length}/200 characters
                    </span>
                  </div>
                  <div className="relative">
                    <textarea
                      value={generatedMessage}
                      onChange={(e) => setGeneratedMessage(e.target.value)}
                      className="w-full p-3 pb-16 border border-gray-300 rounded-md text-sm resize-none"
                      rows={4}
                      maxLength={200}
                    />
                    <button
                      onClick={copyToClipboard}
                      className="absolute bottom-4 right-2 p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 hover:text-gray-700 rounded-md text-xs transition-all duration-200 flex items-center gap-1.5 shadow-xs hover:shadow-md"
                      title="Copy to clipboard"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                        <path d="m4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                      <span className="text-xs font-medium">Copy</span>
                    </button>
                  </div>
                  {generatedMessage.length > 200 && (
                    <p className="text-red-500 text-xs mt-1">Message exceeds 200 character limit</p>
                  )}
                </div>
              )}
            </div>
          )}

          <ul className="space-y-3">
            {questions.map((question, index) => (
              <li key={index} className="flex">
                <div className="me-3 shrink-0">
                  <span className="flex justify-center items-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                    {index + 1}
                  </span>
                </div>
                <p className="text-gray-800">{question}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
});

export default CoffeeChatQuestions;
